import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWishlist } from "@/context/WishlistContext";
import { Navbar } from "@/components/Navbar";
import { GenreColumns } from "@/components/GenreColumns";
import { Heart, ArrowLeft, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";

export default function Wishlist() {
  const { user } = useAuth();
  const { ids } = useWishlist();
  const navigate = useNavigate();
  const [tracks, setTracks] = useState([]);
  const [owned, setOwned] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [reminding, setReminding] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/wishlist").then((r) => setTracks(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/purchases").then((r) => setOwned(new Set(r.data.map((t) => t.id)))).catch(() => {});
  };

  useEffect(() => {
    if (user === false) { navigate("/"); return; }
    if (user) load();
  }, [user, navigate]);

  // Re-load when items are toggled off elsewhere
  useEffect(() => {
    if (user) setTracks((prev) => prev.filter((t) => ids.has(t.id)));
  }, [ids, user]);

  const remindMe = async () => {
    setReminding(true);
    try {
      const { data } = await api.post("/wishlist-remind-me");
      toast.success(`Reminder sent for ${data.count} beat(s)! Check your inbox.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send reminder");
    } finally {
      setReminding(false);
    }
  };

  return (
    <div className="min-h-screen grain pb-32">
      <Navbar />
      <div className="px-6 md:px-12 py-10 relative z-10">
        <button onClick={() => navigate("/")} data-testid="back-home-btn"
          className="flex items-center gap-2 text-sm text-[#9ca3af] hover:text-white transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to catalog
        </button>

        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <Heart className="w-7 h-7 text-[#e2ff31] fill-[#e2ff31]" />
            <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold">My Wishlist</h1>
          </div>
          {tracks.length > 0 && (
            <button data-testid="remind-me-btn" onClick={remindMe} disabled={reminding}
              className="flex items-center gap-2 text-sm rounded-full border border-[#2a2a2a] px-4 py-2.5 hover:bg-[#1e1e1e] transition-colors disabled:opacity-60">
              {reminding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Email me my wishlist
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#e2ff31]" /></div>
        ) : tracks.length === 0 ? (
          <div data-testid="empty-wishlist" className="text-center py-24 border border-dashed border-[#2a2a2a] rounded-2xl">
            <Heart className="w-12 h-12 text-[#2a2a2a] mx-auto mb-4" />
            <p className="text-[#9ca3af] mb-4">Your wishlist is empty. Tap the heart on any beat to save it here.</p>
            <button onClick={() => navigate("/")} className="rounded-full bg-[#e2ff31] text-black hover:bg-[#c8e619] font-semibold px-6 py-2.5 transition-colors">
              Browse beats
            </button>
          </div>
        ) : (
          <>
            <p className="text-[#9ca3af] mb-6">Listen anytime, and buy when you're ready — but remember, every beat is one-of-one.</p>
            <GenreColumns tracks={tracks} owned={owned} />
          </>
        )}
      </div>
    </div>
  );
}
